import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Grid,
    Paper,
    MenuItem,
    FormControl,
    FormLabel,
    FormHelperText,
    Select,
    Chip,
    Alert
} from '@mui/material';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, isValid } from 'date-fns';
import {
    SessionQuestionEntityFrontend,
    AnswerTypeFrontend,
    ListItemFieldDefinitionFrontend,
    QuestionOptionDtoFrontend,
} from '../../api/sessionQuestionsApi.ts';

// Interface for the structure of items in listItemSchema, aligned with ListItemFieldDefinitionFrontend
// and ensuring 'type' is available for the modal's rendering logic.
interface ModalListItemFieldSchema {
    fieldName: string;
    label: string;
    fieldType: AnswerTypeFrontend;
    isRequired?: boolean;
    options?: QuestionOptionDtoFrontend[];
    defaultValue?: any;
    validationRules?: {
        minLength?: number;
        maxLength?: number;
        min?: number;
        max?: number;
        pattern?: string;
        errorMessage?: string;
        dateFormat?: string;
    };
    displayCondition?: {
        dependsOn: string;
        operator: string;
        value: any;
    };
    placeholder?: string;
    helpText?: string;
}



/**
 * Editable variant: session_questions no longer carry an `answer` field post-PR1
 * (answer values live on the worker entity). For in-progress editing (AI suggestions,
 * admin corrections-before-submit), the modal still needs a transient answer to
 * display and modify. Callers attach a local `answer` field; on save the value is
 * submitted via the bulk-update API and routed to the worker.
 */
type EditableSessionQuestion = SessionQuestionEntityFrontend & { answer?: any };

interface ListItemEditModalProps {
    open: boolean;
    onClose: () => void;
    question: EditableSessionQuestion | null;
    onSave: (updatedAnswer: string) => void;
}

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '700px',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto'
};

const ListItemEditModal: React.FC<ListItemEditModalProps> = ({ open, onClose, question, onSave }) => {

    const [editedItems, setEditedItems] = useState<any[]>([]);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [showValidationAlert, setShowValidationAlert] = useState(false);

    useEffect(() => {
        if (open && question && question.answer) {
            try {
                const answerString = typeof question.answer === 'string' ? question.answer : JSON.stringify(question.answer);
                const parsedAnswer = JSON.parse(answerString);
                if (Array.isArray(parsedAnswer)) {
                    setEditedItems(parsedAnswer);
                } else {
                    setEditedItems([]); 
                }
            } catch (error) {
                setEditedItems([]); 
            }
        } else if (open && question && !question.answer) {
             setEditedItems([]); 
        }
    }, [open, question]);

    if (!question || !question.listItemSchema) {
        return null; 
    }

    const schemaFields: ModalListItemFieldSchema[] = question.listItemSchema.map(
        (fieldDef: ListItemFieldDefinitionFrontend) => ({
            fieldName: fieldDef.fieldName,
            label: fieldDef.label,
            fieldType: fieldDef.fieldType || AnswerTypeFrontend.TEXT,
            isRequired: fieldDef.isRequired,
            options: fieldDef.options,
            defaultValue: fieldDef.defaultValue,
            validationRules: fieldDef.validationRules,
            displayCondition: fieldDef.displayCondition,
            placeholder: fieldDef.placeholder,
            helpText: fieldDef.helpText
        } as ModalListItemFieldSchema)
    );

    const handleAddItem = () => {
        if (question?.maxItems && editedItems.length >= question.maxItems) {
            setValidationErrors({ general: `Maximum ${question.maxItems} items allowed` });
            setShowValidationAlert(true);
            setTimeout(() => setShowValidationAlert(false), 3000);
            return;
        }

        const newItem: Record<string, any> = {};
        schemaFields.forEach((field: ModalListItemFieldSchema) => {
            if (field.defaultValue !== undefined) {
                newItem[field.fieldName] = field.defaultValue;
            } else if (field.fieldType.toUpperCase() === AnswerTypeFrontend.BOOLEAN) {
                newItem[field.fieldName] = null;
            } else if (field.fieldType.toUpperCase() === AnswerTypeFrontend.MULTISELECT) {
                newItem[field.fieldName] = [];
            } else if (field.fieldType.toUpperCase() === AnswerTypeFrontend.DATE) {
                newItem[field.fieldName] = null;
            } else {
                newItem[field.fieldName] = '';
            }
        });
        setEditedItems([...editedItems, newItem]);
    };

    const handleDeleteItem = (index: number) => {
        setEditedItems(editedItems.filter((_, i) => i !== index));
    };

    const validateField = (field: ModalListItemFieldSchema, value: any): string | null => {
        const rules = field.validationRules;
        if (!rules) return null;

        const isValueEmpty =
            value === undefined ||
            value === null ||
            (typeof value === 'string' && !value.trim()) ||
            (Array.isArray(value) && value.length === 0);

        // Required field validation
        if (field.isRequired && isValueEmpty) {
            return `${field.label} is required`;
        }

        // Skip other validations if value is empty and field is not required
        if (isValueEmpty) {
            return null;
        }

        // Text field validations
        if (field.fieldType === AnswerTypeFrontend.TEXT || field.fieldType === AnswerTypeFrontend.TEXT_AREA) {
            const strValue = String(value);
            if (rules.minLength && strValue.length < rules.minLength) {
                return rules.errorMessage || `${field.label} must be at least ${rules.minLength} characters`;
            }
            if (rules.maxLength && strValue.length > rules.maxLength) {
                return rules.errorMessage || `${field.label} must be no more than ${rules.maxLength} characters`;
            }
            if (rules.pattern) {
                const regex = new RegExp(rules.pattern);
                if (!regex.test(strValue)) {
                    return rules.errorMessage || `${field.label} format is invalid`;
                }
            }
        }

        // Number field validations
        if (field.fieldType === AnswerTypeFrontend.NUMBER) {
            const numValue = Number(value);
            if (isNaN(numValue)) {
                return `${field.label} must be a valid number`;
            }
            if (rules.min !== undefined && numValue < rules.min) {
                return rules.errorMessage || `${field.label} must be at least ${rules.min}`;
            }
            if (rules.max !== undefined && numValue > rules.max) {
                return rules.errorMessage || `${field.label} must be no more than ${rules.max}`;
            }
        }

        return null;
    };

    const handleItemChange = (index: number, fieldName: string, value: any) => {
        const updatedItems = [...editedItems];
        updatedItems[index] = { ...updatedItems[index], [fieldName]: value };
        setEditedItems(updatedItems);

        // Validate the field
        const field = schemaFields.find(f => f.fieldName === fieldName);
        if (field) {
            const error = validateField(field, value);
            const errorKey = `${index}-${fieldName}`;
            if (error) {
                setValidationErrors(prev => ({ ...prev, [errorKey]: error }));
            } else {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[errorKey];
                    return newErrors;
                });
            }
        }
    };

    const validateAllItems = (): boolean => {
        let isValid = true;
        const errors: Record<string, string> = {};

        // Check minimum items
        if (question?.minItems && editedItems.length < question.minItems) {
            errors.general = `At least ${question.minItems} items are required`;
            isValid = false;
        }

        // Validate each item
        editedItems.forEach((item, itemIndex) => {
            schemaFields.forEach(field => {
                const error = validateField(field, item[field.fieldName]);
                if (error) {
                    errors[`${itemIndex}-${field.fieldName}`] = error;
                    isValid = false;
                }
            });
        });

        setValidationErrors(errors);
        return isValid;
    };

    const handleSave = () => {
        if (!validateAllItems()) {
            setShowValidationAlert(true);
            return;
        }
        onSave(JSON.stringify(editedItems));
        onClose();
    };
    
    const shouldShowField = (fieldSchema: ModalListItemFieldSchema, item: Record<string, any>): boolean => {
        if (!fieldSchema.displayCondition) return true;
        
        const { dependsOn, operator, value: conditionValue } = fieldSchema.displayCondition;
        const dependentValue = item[dependsOn];
        
        switch (operator) {
            case 'equals':
                return dependentValue === conditionValue;
            case 'notEquals':
                return dependentValue !== conditionValue;
            case 'contains':
                return String(dependentValue).includes(String(conditionValue));
            case 'greaterThan':
                return Number(dependentValue) > Number(conditionValue);
            case 'lessThan':
                return Number(dependentValue) < Number(conditionValue);
            case 'in':
                return Array.isArray(conditionValue) && conditionValue.includes(dependentValue);
            case 'notIn':
                return Array.isArray(conditionValue) && !conditionValue.includes(dependentValue);
            default:
                return true;
        }
    };

    const renderField = (item: Record<string, any>, fieldSchema: ModalListItemFieldSchema, itemIndex: number) => { 
        if (!shouldShowField(fieldSchema, item)) {
            return null;
        }

        const value = item[fieldSchema.fieldName]; 
        const fieldTypeEnum = fieldSchema.fieldType.toUpperCase() as AnswerTypeFrontend;
        const errorKey = `${itemIndex}-${fieldSchema.fieldName}`;
        const hasError = !!validationErrors[errorKey];
        const errorMessage = validationErrors[errorKey];

        const commonProps = {
            fullWidth: true,
            margin: "normal" as const,
            size: "small" as const,
            error: hasError,
            helperText: errorMessage || fieldSchema.helpText,
            placeholder: fieldSchema.placeholder
        };

        switch (fieldTypeEnum) {
            case AnswerTypeFrontend.TEXT:
                return (
                    <TextField
                        {...commonProps}
                        label={fieldSchema.label}
                        value={value || ''}
                        onChange={(e) => handleItemChange(itemIndex, fieldSchema.fieldName, e.target.value)}
                        inputProps={{
                            maxLength: fieldSchema.validationRules?.maxLength
                        }}
                    />
                );
                
            case AnswerTypeFrontend.TEXT_AREA:
                return (
                    <TextField
                        {...commonProps}
                        label={fieldSchema.label}
                        value={value || ''}
                        onChange={(e) => handleItemChange(itemIndex, fieldSchema.fieldName, e.target.value)}
                        multiline
                        rows={3}
                        inputProps={{
                            maxLength: fieldSchema.validationRules?.maxLength
                        }}
                    />
                );
                
            case AnswerTypeFrontend.NUMBER:
                return (
                    <TextField
                        {...commonProps}
                        label={fieldSchema.label}
                        value={value || ''}
                        type="number"
                        onChange={(e) => handleItemChange(itemIndex, fieldSchema.fieldName, e.target.value)}
                        inputProps={{
                            min: fieldSchema.validationRules?.min,
                            max: fieldSchema.validationRules?.max
                        }}
                    />
                );
                
            case AnswerTypeFrontend.BOOLEAN:
                return (
                    <TextField
                        {...commonProps}
                        select
                        label={fieldSchema.label}
                        value={value === null || value === undefined ? '' : value ? 'true' : 'false'}
                        onChange={(e) => {
                            const selected = e.target.value;
                            handleItemChange(
                                itemIndex,
                                fieldSchema.fieldName,
                                selected === '' ? null : selected === 'true'
                            );
                        }}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                    </TextField>
                );
                
            case AnswerTypeFrontend.DATE:
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label={fieldSchema.label}
                            value={value ? parse(value, fieldSchema.validationRules?.dateFormat || 'yyyy-MM-dd', new Date()) : null}
                            onChange={(newValue) => {
                                if (newValue && isValid(newValue)) {
                                    const formatted = format(newValue, fieldSchema.validationRules?.dateFormat || 'yyyy-MM-dd');
                                    handleItemChange(itemIndex, fieldSchema.fieldName, formatted);
                                } else {
                                    handleItemChange(itemIndex, fieldSchema.fieldName, null);
                                }
                            }}
                            enableAccessibleFieldDOMStructure={false}
                            slotProps={{
                                textField: {
                                    ...commonProps,
                                    InputLabelProps: { shrink: true }
                                }
                            }}
                        />
                    </LocalizationProvider>
                );
                
            case AnswerTypeFrontend.RADIO:
                return (
                    <FormControl {...commonProps}>
                        <FormLabel component="legend">{fieldSchema.label}</FormLabel>
                        <Select
                            value={value || ''}
                            onChange={(e) => handleItemChange(itemIndex, fieldSchema.fieldName, e.target.value)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {fieldSchema.options?.map(option => (
                                <MenuItem key={option.key} value={option.key}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {(hasError || fieldSchema.helpText) && (
                            <FormHelperText>{errorMessage || fieldSchema.helpText}</FormHelperText>
                        )}
                    </FormControl>
                );
                
            case AnswerTypeFrontend.MULTISELECT:
                return (
                    <FormControl {...commonProps}>
                        <FormLabel component="legend">{fieldSchema.label}</FormLabel>
                        <Select
                            multiple
                            value={value || []}
                            onChange={(e) => handleItemChange(itemIndex, fieldSchema.fieldName, e.target.value)}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map((val) => {
                                        const option = fieldSchema.options?.find(opt => opt.key === val);
                                        return <Chip key={val} label={option?.label || val} size="small" />;
                                    })}
                                </Box>
                            )}
                        >
                            {fieldSchema.options?.map(option => (
                                <MenuItem key={option.key} value={option.key}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {(hasError || fieldSchema.helpText) && (
                            <FormHelperText>{errorMessage || fieldSchema.helpText}</FormHelperText>
                        )}
                    </FormControl>
                );
                
            default:
                if (Object.values(AnswerTypeFrontend).includes(fieldSchema.fieldType as AnswerTypeFrontend)) {
                    return (
                        <TextField
                            {...commonProps}
                            label={`${fieldSchema.label} (Unsupported edit type: ${fieldSchema.fieldType})`}
                            value={typeof value === 'object' ? JSON.stringify(value) : value || ''}
                            disabled
                        />
                    );
                }
                return <Typography variant="caption">Invalid field type in schema: {fieldSchema.fieldType}</Typography>;
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="list-item-edit-modal-title"
            aria-describedby="list-item-edit-modal-description"
        >
            <Box sx={modalStyle}>
                <Typography id="list-item-edit-modal-title" variant="h6" component="h2">
                    Edit Items for: {question.question}
                </Typography>
                
                {(question.minItems || question.maxItems) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {question.minItems && `Minimum items: ${question.minItems}`}
                        {question.minItems && question.maxItems && ' • '}
                        {question.maxItems && `Maximum items: ${question.maxItems}`}
                    </Typography>
                )}

                {showValidationAlert && validationErrors.general && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setShowValidationAlert(false)}>
                        {validationErrors.general}
                    </Alert>
                )}

                <Box sx={{ my: 2, maxHeight: '60vh', overflowY: 'auto' }}>
                    {editedItems.map((item, itemIndex) => (
                        <Paper elevation={2} sx={{ p: 2, mb: 2 }} key={itemIndex}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={11}>
                                    {schemaFields.map((fieldSch: ModalListItemFieldSchema) => {
                                        const field = renderField(item, fieldSch, itemIndex);
                                        return field ? (
                                            <Grid item xs={12} key={fieldSch.fieldName}>
                                                {field}
                                            </Grid>
                                        ) : null;
                                    })}
                                </Grid>
                                <Grid item xs={1} container justifyContent="center">
                                    <IconButton onClick={() => handleDeleteItem(itemIndex)} color="error">
                                        <DeleteOutline />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                    <Button
                        startIcon={<AddCircleOutline />}
                        onClick={handleAddItem}
                        variant="outlined"
                        sx={{ mt: 1 }}
                    >
                        Add Item
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ListItemEditModal; 
